import type { SafeAny } from '@/common/types';
import { EventBus, Subscriptions } from '@/common/utils';
import { select, scaleLinear } from 'd3';
import { debounce } from 'lodash';
import type { GraphPoint, Selection } from './d3-client.interface';

const DEFAULT_SVG_SIZES = {
  width: 1250,
  height: 600,
  x: 12,
  y: 1023
};

export abstract class BaseD3 {
  protected container: SVGElement;
  protected svg: Selection;
  protected graphGroup!: Selection;
  protected grid!: Selection;
  protected yGridLines!: Selection;
  protected xGridLines!: Selection;
  protected tooltipLayer!: Selection;
  protected tooltip!: Selection;
  protected xAxisGroup!: Selection;
  protected yAxisGroup!: Selection;
  protected data!: SafeAny;
  protected svgSizes!: { width: number; height: number; x: number; y: number };
  protected xScale = scaleLinear();
  protected readonly debouncedUpdateSvgSizes;
  protected eventBus = new EventBus();
  protected subscriptions = new Subscriptions();
  // ResizeObserver complements the window.resize listener. Window resize only
  // fires when the viewport itself changes (e.g., DevTools docked to the
  // side), not when the layout *inside* the page reflows — adding a
  // full-night signal panel to the page, an inner scrollbar appearing or
  // disappearing, ancestor flex/grid recalculations, etc. Without observing
  // the SVG container directly, those internal changes update the visible
  // box but leave the chart's cached width stale, so the next paint draws
  // the line at the old range while the axes have already moved.
  private resizeObserver: ResizeObserver | null = null;

  protected constructor(svgRef: SVGElement) {
    this.container = svgRef;
    this.svg = select(svgRef);
    this.debouncedUpdateSvgSizes = debounce(() => this.updateSvgSizes(true), 50);

    this.showTooltip = this.showTooltip.bind(this);
    this.updateTooltip = this.updateTooltip.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
  }
  buildSvgElements() {
    this.selectGlobalTooltip();

    this.grid = this.svg.append('g').classed('grid', true);
    this.yGridLines = this.grid.append('g').classed('y-lines', true);
    this.xGridLines = this.grid.append('g').classed('x-lines', true);
  }

  attachWindowResizeEvent() {
    window.addEventListener('resize', this.debouncedUpdateSvgSizes);

    // Observe the actual SVG element so internal layout changes (full-night
    // signal panels added/removed, scrollbar appearing/disappearing,
    // ancestor flex recalculation, DevTools docked at bottom not changing
    // viewport width but still reflowing) trigger a size refresh too. Both
    // listeners route through the same `debouncedUpdateSvgSizes` so duplicate
    // events from window+observer collapse into one read.
    if (typeof ResizeObserver !== 'undefined' && this.container) {
      this.resizeObserver = new ResizeObserver(() => this.debouncedUpdateSvgSizes());
      this.resizeObserver.observe(this.container);
    }

    return this;
  }

  detachWindowResizeEvent() {
    window.removeEventListener('resize', this.debouncedUpdateSvgSizes);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    return this;
  }

  unsubscribe() {
    this.subscriptions.unsubscribe();
  }

  cleanUp(): void {
    this.detachWindowResizeEvent();
    this.unsubscribe();
  }

  protected selectGlobalTooltip(): void {
    this.tooltip = select('.d3-tooltip');
    if (!this.tooltip.node()) {
      this.tooltip = select('body').append('div').classed('d3-tooltip', true);
    }
  }

  protected updateSvgSizes(forceRead = false) {
    if (!this.container || !this.container.getBoundingClientRect) {
      this.svgSizes = {
        width: DEFAULT_SVG_SIZES.width,
        height: DEFAULT_SVG_SIZES.height,
        y: DEFAULT_SVG_SIZES.y,
        x: DEFAULT_SVG_SIZES.x
      };
      return;
    }

    // Skip expensive getBoundingClientRect when sizes are already known and
    // no resize occurred. During epoch navigation ~16 signal graphs each
    // calling getBoundingClientRect causes forced reflow (layout thrashing).
    if (!forceRead && this.svgSizes && this.svgSizes.width > 1 && this.svgSizes.height > 1) {
      return;
    }

    const containerSizes = this.container.getBoundingClientRect();

    this.svgSizes = {
      width: containerSizes.width || DEFAULT_SVG_SIZES.width,
      height: containerSizes.height || DEFAULT_SVG_SIZES.height,
      y: containerSizes.y || DEFAULT_SVG_SIZES.height,
      x: containerSizes.x || DEFAULT_SVG_SIZES.x
    };

    this.svg.attr('viewBox', `0 0 ${this.svgSizes.width} ${this.svgSizes.height}`);
  }

  protected showTooltip(): void {
    if (this.isEventDetailsModalVisible()) return;

    this.tooltip.style('display', 'flex');
  }

  protected updateTooltip(event: MouseEvent): void {
    const targetRect = (event.target as SafeAny).getBoundingClientRect();
    const data = this.findTheClosesPoint(
      this.xScale.invert(event.offsetX - targetRect.x + this.svgSizes.x)
    );
    if (!data) {
      return;
    }
    this.tooltip
      .style('top', `${event.y + 5}px`)
      .html(this.buildTooltipContent(data))
      .style('left', `${event.x + 5}px`);

    requestAnimationFrame(() => {
      this.tooltip.style(
        'transform',
        event.x + (this.tooltip.node()?.getBoundingClientRect().width || 0) > window.innerWidth - 20
          ? `translateX(-100%)`
          : ''
      );
    });
  }

  protected hideTooltip(): void {
    this.tooltip.style('display', null).html('');
  }

  protected buildTooltipContent(_data: SafeAny): string {
    return '';
  }

  protected findTheClosesPoint(x: number): GraphPoint<SafeAny> | SafeAny {
    return this.data?.reduce(
      (closes: GraphPoint<SafeAny>, item: SafeAny) => {
        if (Math.abs(closes.x - x) > Math.abs(item.x - x)) {
          return item;
        }

        return closes;
      },
      { x: 0, y: 0 }
    );
  }

  protected getRangeFromBrush(selection: [number, number]): [number, number] | null {
    if (!selection) {
      return null;
    }
    const start = selection[0] < selection[1] ? selection[0] : selection[1];
    const end = selection[0] > selection[1] ? selection[0] : selection[1];

    return [start, end];
  }

  protected isEventDetailsModalVisible = (): boolean => {
    const el = document.querySelector('.event-details');

    if (!el) return false;

    const style = getComputedStyle(el);

    return (
      style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) !== 0
    );
  };
}
